import { useMutation } from '@tanstack/react-query'
import type { SubmitEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'

import { forgotPassword } from './api'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface ForgotPasswordPageProps {
  readonly onNavigateToLogin: () => void
}

export function ForgotPasswordPage({ onNavigateToLogin }: ForgotPasswordPageProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | undefined>(undefined)

  const mutation = useMutation({
    mutationFn: forgotPassword,
  })

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()

    if (!EMAIL_PATTERN.test(email)) {
      setEmailError(t('auth.forgot.errors.emailRequired'))
      return
    }

    setEmailError(undefined)
    mutation.mutate({ email })
  }

  const apiMessages = describeApiError(mutation.error)
  const [firstMessage, ...restMessages] = apiMessages

  return (
    <main className="flex min-h-full items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>{t('auth.forgot.title')}</CardTitle>
            <CardDescription>{t('auth.forgot.subtitle')}</CardDescription>
          </CardHeader>
          {mutation.isSuccess ? (
            <CardContent className="flex flex-col gap-4">
              <Alert variant="success" title={t('auth.forgot.sentTitle')}>
                {t('auth.forgot.sentDescription')}
              </Alert>
              <Button variant="secondary" className="w-full" onClick={onNavigateToLogin}>
                {t('auth.forgot.backToLogin')}
              </Button>
            </CardContent>
          ) : (
            <form
              onSubmit={(event) => {
                handleSubmit(event)
              }}
              noValidate
            >
              <CardContent className="flex flex-col gap-4">
                {firstMessage !== undefined ? (
                  <Alert variant="danger" title={firstMessage}>
                    {restMessages.length > 0 ? (
                      <ul className="list-disc pl-4">
                        {restMessages.map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                    ) : null}
                  </Alert>
                ) : null}
                <Input
                  id="forgot-email"
                  type="email"
                  label={t('auth.forgot.emailLabel')}
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                  }}
                  error={emailError}
                />
              </CardContent>
              <CardFooter className="flex-col items-stretch gap-3">
                <Button type="submit" loading={mutation.isPending} className="w-full">
                  {mutation.isPending ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
                </Button>
                <button
                  type="button"
                  onClick={onNavigateToLogin}
                  className="text-center text-sm font-medium text-action-primary hover:underline"
                >
                  {t('auth.forgot.backToLogin')}
                </button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </main>
  )
}
